import { create } from "zustand";
import { DEFAULT_CHART_SETTINGS, MAX_ACTIVE_SERIES, SERIES_SIDEBAR_WIDTH, VISIBLE_ON_START } from "../constants";
import type { SavedTrend } from "../data/savedTrends";
import type { DashboardSeries } from "../lib/dashboardSeries";
import {
  fromTrendConfig,
  toTrendConfig,
  TREND_NAME_MAX_LENGTH,
  type TrendConfig,
  type TrendPage,
} from "../lib/trendConfig";
import * as ops from "../lib/trendOps";
import type {
  ChartSettings,
  LineStyleKey,
  SeriesKey,
  Chart,
  TrendRange,
  TrendScale,
  TrendVariant,
} from "../types";

/**
 * State for the Trends page, shared by the sidebar panel and the main view.
 * A dashboard's "See as Trend" button fills it (`openFromDashboard`) with the
 * dashboard's series and their saved variants: they're the series the page
 * can add, and once their gauge configs have loaded the first six the viewer
 * can see start the page. At most six series are active at once.
 *
 * Saving the page makes it a saved trend (`saved`), its own object: the
 * dashboard it may have come from is dropped, along with the add-series list
 * that dashboard offered. "See as Trend" always starts a fresh preview, never
 * a saved trend.
 *
 * Series configs aren't kept here: they're read from the gauge series cache
 * (`seriesRows`). Only explicit variants are: one the page was opened with and
 * one the user picked. A series with neither shows its default variant. A
 * trend's or the page's variant is derived from its series and shows "mixed"
 * when they differ.
 */

export interface TrendDrag {
  fromChartId: string | null;
  displayId: string;
}

/** Where a click that opens Trends with series came from, for analytics. */
export type TrendsEntryPoint = "dashboard_toolbar" | "gauge_context_menu" | "search_result_menu";

/** The saved trend the page shows. */
export interface SavedTrendRef {
  id: string;
  name: string;
  /** Its config as last saved, as JSON, to tell whether the page has changed since. */
  config: string;
  /** When it was last saved, so a save can be refused if it changed elsewhere since. */
  updatedAt?: string;
}

/**
 * What the Trends sidebar shows: the user's saved trends, or the open page's
 * charts and series.
 */
export type TrendsSidebarPanel = "saved" | "page";

/** Drop target id for the "new trend" zone at the end of the page. */
export const NEW_CHART_ZONE = "new";

/** Drop target id for the gap before the trend at `index`: a new trend there. */
export const chartGapZone = (index: number) => `gap:${index}`;
const gapIndex = (targetId: string) => (targetId.startsWith("gap:") ? Number(targetId.slice(4)) : null);

interface TrendsData {
  /** Every series on the dashboard the page was opened from, in dashboard order. */
  dashboardSeries: string[];
  /** That dashboard's name, shown as the page title. */
  dashboardTitle: string | null;
  /** The page it was opened from, for the toolbar's way back. */
  dashboardHref: string | null;
  /**
   * The page was opened from a dashboard with none of its series in the
   * viewer's plan. Shows a notice once, until it's dismissed.
   */
  lockedNotice: boolean;
  /**
   * The variant each series starts on, when the page was opened with one: the
   * variant saved on its dashboard gauge, when Trends can show it.
   */
  startingVariant: Record<string, TrendVariant>;
  /**
   * Opened from a dashboard whose series are still being looked up; the page
   * starts once they've all loaded or failed (`startFromDashboard`). Null otherwise.
   * `clickedFrom`: opened by a click, which raises the locked notice when
   * none of the series are in the viewer's plan.
   */
  pendingStart: { clickedFrom?: TrendsEntryPoint } | null;
  /** The saved trend the page shows; null on a preview. */
  saved: SavedTrendRef | null;
  /**
   * The page's name, as the user named it or as its saved trend is called.
   * Null on a preview nobody has named: it goes by its dashboard's name. A
   * new name counts as an unsaved change until it's saved.
   */
  trendName: string | null;
  /** Opening a trend or a dashboard's preview shows its charts; Back goes to the saved list. */
  sidebarPanel: TrendsSidebarPanel;
  /**
   * A saved trend the user picked that hasn't loaded yet. Until it has, the
   * sidebar shows it loading rather than the trend that's still in the store.
   */
  openingTrendId: string | null;
  /**
   * Goes up each time the page is replaced by another trend (opened, started
   * or dropped), so work that began on the old page, like a save on its way,
   * can tell it no longer applies.
   */
  pageGeneration: number;
  charts: Chart[];
  nextId: number;
  /** The variant the user picked for a series on this page, over its starting variant. */
  chosenVariant: Record<string, TrendVariant>;
  scale: TrendScale;
  range: TrendRange;
  lineStyle: Record<string, LineStyleKey>;
  chartSettings: ChartSettings;
  collapsed: Record<string, boolean>;
  /** Trends folded down to their header in the left sidebar. */
  sidebarCollapsed: Record<string, true>;
  /**
   * Series hidden from their trend's chart, by `trendId:displayId`. A hidden
   * series stays in the trend (and counts toward the six) with its readout.
   */
  hidden: Record<SeriesKey, true>;
  /** Shared by every trend card so stacked sidebars line up. */
  seriesSidebarWidth: number;
  /** Charts shown enlarged, in the order they were enlarged; any number at once. */
  expandedChartIds: string[];
  /** Series options menu open from a sidebar row. */
  openMenu: SeriesKey | null;
  /** Series options menu open from a chart readout. */
  openReadout: SeriesKey | null;
  drag: TrendDrag | null;
  /** Chart id, NEW_CHART_ZONE or a `chartGapZone` currently under a drag. */
  dragOver: string | null;
}

interface TrendsActions {
  /**
   * Starts the page over from a dashboard's series, each on its gauge's saved
   * variant, with no trends until their configs load (`pendingStart`).
   * Chart settings are kept. `clickedFrom`: a click on "See as Trend" rather
   * than a page load.
   */
  openFromDashboard: (
    series: DashboardSeries[],
    title?: string,
    options?: { clickedFrom?: TrendsEntryPoint },
  ) => void;
  /**
   * Starts a pending page: the dashboard's first six series not `isLocked`, in
   * one trend, with all but the first three hidden. Locked series stay on
   * offer but never start on the page. A chart the user already started while
   * waiting is kept instead.
   */
  startFromDashboard: (isLocked: (displayId: string) => boolean) => void;
  setDashboardTitle: (title: string | null) => void;
  setDashboardHref: (href: string | null) => void;
  dismissLockedNotice: () => void;
  /**
   * Opens a saved trend: its charts, series and settings, and nothing from a
   * dashboard. The sidebar width is kept.
   */
  loadSaved: (row: Pick<SavedTrend, "id" | "name" | "config"> & { updated_at?: string }) => void;
  /**
   * The page was just saved as this trend (created or updated), with
   * `config`: the config that was sent, so edits made while it saved still
   * count as unsaved. From now on the page is that trend, with no dashboard
   * behind it: the dashboard's starting variants become the page's own, and
   * its add-series list goes.
   */
  markSaved: (trend: { id: string; name: string; config: TrendConfig; updatedAt?: string }) => void;
  /** Puts the open saved trend back as it was last saved, dropping its unsaved changes. */
  revertToSaved: () => void;
  /** Drops the saved trend (it was deleted): the page starts over empty, as `clearAll` does. */
  discardSaved: () => void;
  /** Names the page; an empty name goes back to its saved or dashboard name. */
  setTrendName: (name: string | null) => void;
  /**
   * The open saved trend was renamed and the new name saved (from the saved
   * list). The page takes the name too, unless it has an unsaved name of its own.
   */
  renamedSaved: (name: string, updatedAt?: string) => void;
  /** Names a chart; an empty name goes back to its automatic one. */
  renameChart: (chartId: string, name: string | null) => void;
  showSidebarPanel: (panel: TrendsSidebarPanel) => void;
  /** The user picked a saved trend: the sidebar goes to its charts, loading until `loadSaved` brings them. */
  startOpeningSaved: (id: string) => void;
  /** Picking it didn't open it (its load failed): the sidebar stops waiting for it. */
  stopOpeningSaved: () => void;
  /**
   * Starts a new, empty trend: nothing saved, no dashboard, and the sidebar on
   * its charts, ready for the first one. Chart settings and the sidebar width
   * are kept.
   */
  startNewTrend: () => void;

  moveSeries: (fromId: string | null, displayId: string, toId: string) => void;
  newChartFrom: (displayId: string, fromId: string | null) => void;
  splitChart: (chartId: string) => void;
  splitAll: () => void;
  mergeAll: () => void;
  mergeUp: (chartId: string) => void;
  removeSeries: (chartId: string, displayId: string) => void;
  addSeries: (chartId: string, displayId: string) => void;
  deleteChart: (chartId: string) => void;

  setSeriesVariant: (displayId: string, variant: TrendVariant) => void;
  setChartVariant: (chartId: string, variant: TrendVariant) => void;
  setAllVariant: (variant: TrendVariant) => void;
  setChartScale: (chartId: string, scale: TrendScale) => void;
  setAllScale: (scale: TrendScale) => void;
  /** The date range for every chart: charts with their own go back to the page's. */
  setRange: (range: TrendRange) => void;
  /** A chart's own date range; the page's range takes it back to following the page. */
  setChartRange: (chartId: string, range: TrendRange) => void;
  setLineStyle: (displayId: string, style: LineStyleKey) => void;
  resetLineStyle: (displayId: string) => void;
  setChartSetting: <K extends keyof ChartSettings>(key: K, value: ChartSettings[K]) => void;
  resetChartSettings: () => void;

  toggleCollapsed: (chartId: string) => void;
  toggleSidebarCollapsed: (chartId: string) => void;
  /** Folds every trend in the left sidebar, or unfolds them all. */
  setAllSidebarCollapsed: (collapsed: boolean) => void;
  setSeriesHidden: (chartId: string, displayId: string, hidden: boolean) => void;
  /** Clamped to SERIES_SIDEBAR_WIDTH.min–max. */
  setSeriesSidebarWidth: (width: number) => void;
  toggleExpanded: (chartId: string) => void;
  setOpenMenu: (key: SeriesKey | null) => void;
  setOpenReadout: (key: SeriesKey | null) => void;

  startDrag: (drag: TrendDrag) => void;
  setDragOver: (targetId: string | null) => void;
  endDrag: () => void;
  /** Drops the dragged series on a chart, on NEW_CHART_ZONE, or on a `chartGapZone`. */
  dropOn: (targetId: string) => void;

  /**
   * Starts the page over with no trends and no dashboard. Chart settings and
   * the sidebar width are kept, and so is the saved trend the page shows: it's
   * only emptied once the user saves.
   */
  clearAll: () => void;
  reset: () => void;
}

export type TrendsState = TrendsData & TrendsActions;

export const initialTrendsData = (): TrendsData => ({
  dashboardSeries: [],
  dashboardTitle: null,
  dashboardHref: null,
  lockedNotice: false,
  startingVariant: {},
  pendingStart: null,
  saved: null,
  trendName: null,
  sidebarPanel: "saved",
  openingTrendId: null,
  pageGeneration: 0,
  charts: [],
  nextId: 1,
  chosenVariant: {},
  scale: "values",
  range: "5",
  lineStyle: {},
  chartSettings: { ...DEFAULT_CHART_SETTINGS, bandColors: [...DEFAULT_CHART_SETTINGS.bandColors] },
  collapsed: {},
  sidebarCollapsed: {},
  hidden: {},
  seriesSidebarWidth: SERIES_SIDEBAR_WIDTH.default,
  expandedChartIds: [],
  openMenu: null,
  openReadout: null,
  drag: null,
  dragOver: null,
});

/** The trend without a date range of its own. */
function followPageRange(chart: Chart): Chart {
  if (!chart.rangeOverride) return chart;
  const follows = { ...chart };
  delete follows.rangeOverride;
  return follows;
}

/** Sets these series' variant as the user's choice. */
function chooseVariant(state: TrendsData, displayIds: string[], variant: TrendVariant): Partial<TrendsData> {
  return { chosenVariant: { ...state.chosenVariant, ...Object.fromEntries(displayIds.map((id) => [id, variant])) } };
}

/** Runs a trend operation that may create trends, handing it an id generator. */
function applyOp(state: TrendsData, op: (makeId: () => string) => Chart[]): Partial<TrendsData> {
  let next = state.nextId;
  const charts = op(() => `t${next++}`);
  const ids = new Set(charts.map((t) => t.id));
  const stillOpen = (key: SeriesKey | null) => {
    if (!key) return null;
    const [chartId, displayId] = key.split(":");
    return charts.some((t) => t.id === chartId && t.seriesIds.includes(displayId)) ? key : null;
  };
  // Hiding belongs to a series in a trend; it ends when the series leaves.
  const hidden = Object.fromEntries(
    Object.keys(state.hidden)
      .filter((key) => stillOpen(key as SeriesKey))
      .map((key) => [key, true as const]),
  );
  return {
    charts,
    nextId: next,
    hidden,
    openMenu: stillOpen(state.openMenu),
    openReadout: stillOpen(state.openReadout),
    expandedChartIds: state.expandedChartIds.filter((id) => ids.has(id)),
  };
}

/** A fresh, empty page that keeps the user's chart settings and sidebar width. */
function emptyPage(state: TrendsData): TrendsData {
  return {
    ...initialTrendsData(),
    chartSettings: state.chartSettings,
    seriesSidebarWidth: state.seriesSidebarWidth,
    pageGeneration: state.pageGeneration + 1,
  };
}

/** The page a saved trend opens: its charts and settings, and nothing from a dashboard. */
function pageFromSaved(
  state: TrendsData,
  row: Pick<SavedTrend, "id" | "name" | "config"> & { updated_at?: string },
): Partial<TrendsData> {
  const page = {
    ...initialTrendsData(),
    seriesSidebarWidth: state.seriesSidebarWidth,
    pageGeneration: state.pageGeneration + 1,
    ...fromTrendConfig(row.config),
  };
  return {
    ...page,
    trendName: row.name,
    sidebarPanel: "page",
    saved: {
      id: row.id,
      name: row.name,
      config: savedConfigOf(page),
      ...(row.updated_at ? { updatedAt: row.updated_at } : {}),
    },
  };
}

export const useTrendsStore = create<TrendsState>()((set) => ({
  ...initialTrendsData(),

  openFromDashboard: (series, title, options) =>
    set((s) => ({
      ...emptyPage(s),
      dashboardSeries: series.map((x) => x.displayId),
      startingVariant: Object.fromEntries(series.flatMap((x) => (x.variant ? [[x.displayId, x.variant]] : []))),
      dashboardTitle: title?.trim() || null,
      sidebarPanel: "page",
      pendingStart: { ...(options?.clickedFrom ? { clickedFrom: options.clickedFrom } : {}) },
    })),
  startFromDashboard: (isLocked) =>
    set((s) => {
      if (!s.pendingStart) return {};
      // The user started a chart while the series loaded: leave theirs alone.
      if (s.charts.length > 0) return { pendingStart: null };
      const active = s.dashboardSeries.filter((id) => !isLocked(id)).slice(0, MAX_ACTIVE_SERIES);
      return {
        pendingStart: null,
        lockedNotice: !!s.pendingStart.clickedFrom && s.dashboardSeries.length > 0 && active.length === 0,
        charts: active.length > 0 ? [{ id: "t1", seriesIds: active }] : [],
        nextId: 2,
        // The rest are on the chart, ready to show, with their readouts.
        hidden: Object.fromEntries(
          active.slice(VISIBLE_ON_START).map((id) => [ops.seriesKey("t1", id), true as const]),
        ),
      };
    }),
  setDashboardTitle: (title) => set({ dashboardTitle: title?.trim() || null }),
  setDashboardHref: (href) => set({ dashboardHref: href }),
  dismissLockedNotice: () => set({ lockedNotice: false }),
  loadSaved: (row) => set((s) => pageFromSaved(s, row)),
  revertToSaved: () =>
    set((s) =>
      s.saved
        ? pageFromSaved(s, {
            id: s.saved.id,
            name: s.saved.name,
            config: JSON.parse(s.saved.config),
            updated_at: s.saved.updatedAt,
          })
        : {},
    ),
  markSaved: ({ id, name, config, updatedAt }) =>
    set((s) => {
      const detached: Partial<TrendsData> = {
        chosenVariant: { ...s.startingVariant, ...s.chosenVariant },
        startingVariant: {},
        dashboardSeries: [],
        dashboardTitle: null,
        dashboardHref: null,
        lockedNotice: false,
        pendingStart: null,
      };
      return {
        ...detached,
        trendName: name,
        saved: { id, name, config: JSON.stringify(config), ...(updatedAt ? { updatedAt } : {}) },
      };
    }),
  discardSaved: () => set(emptyPage),
  setTrendName: (name) =>
    set((s) => ({ trendName: name?.trim().slice(0, TREND_NAME_MAX_LENGTH) || s.saved?.name || null })),
  renameChart: (chartId, name) => set((s) => ({ charts: ops.renameChart(s.charts, chartId, name) })),
  renamedSaved: (name, updatedAt) =>
    set((s) =>
      s.saved
        ? {
            saved: { ...s.saved, name, ...(updatedAt ? { updatedAt } : {}) },
            trendName: s.trendName === s.saved.name ? name : s.trendName,
          }
        : {},
    ),
  showSidebarPanel: (panel) => set({ sidebarPanel: panel }),
  startOpeningSaved: (id) =>
    set((s) => ({ sidebarPanel: "page", openingTrendId: s.saved?.id === id ? null : id })),
  stopOpeningSaved: () => set({ openingTrendId: null }),
  startNewTrend: () => set((s) => ({ ...emptyPage(s), sidebarPanel: "page" })),

  moveSeries: (fromId, displayId, toId) =>
    set((s) => applyOp(s, () => ops.moveSeries(s.charts, fromId, displayId, toId))),
  newChartFrom: (displayId, fromId) =>
    set((s) => applyOp(s, (makeId) => ops.newChartFrom(s.charts, displayId, fromId, makeId))),
  splitChart: (chartId) => set((s) => applyOp(s, (makeId) => ops.splitChart(s.charts, chartId, makeId))),
  splitAll: () => set((s) => applyOp(s, (makeId) => ops.splitAll(s.charts, makeId))),
  mergeAll: () => set((s) => applyOp(s, () => ops.mergeAll(s.charts))),
  mergeUp: (chartId) => set((s) => applyOp(s, () => ops.mergeUp(s.charts, chartId))),
  removeSeries: (chartId, displayId) =>
    set((s) => applyOp(s, () => ops.removeSeries(s.charts, chartId, displayId))),
  addSeries: (chartId, displayId) => set((s) => applyOp(s, () => ops.addSeries(s.charts, chartId, displayId))),
  deleteChart: (chartId) => set((s) => applyOp(s, () => ops.deleteChart(s.charts, chartId))),

  setSeriesVariant: (displayId, variant) => set((s) => chooseVariant(s, [displayId], variant)),
  setChartVariant: (chartId, variant) =>
    set((s) => {
      const chart = s.charts.find((t) => t.id === chartId);
      return chart ? chooseVariant(s, chart.seriesIds, variant) : {};
    }),
  setAllVariant: (variant) => set((s) => chooseVariant(s, ops.activeSeriesIds(s.charts), variant)),
  setChartScale: (chartId, scale) =>
    set((s) => ({
      charts: s.charts.map((t) =>
        t.id === chartId ? { ...t, scaleOverride: scale === s.scale ? undefined : scale } : t,
      ),
    })),
  setAllScale: (scale) =>
    set((s) => ({ scale, charts: s.charts.map((t) => ({ ...t, scaleOverride: undefined })) })),
  setRange: (range) =>
    set((s) => ({
      range,
      charts: s.charts.some((t) => t.rangeOverride) ? s.charts.map(followPageRange) : s.charts,
    })),
  setChartRange: (chartId, range) =>
    set((s) => ({
      charts: s.charts.map((t) =>
        t.id !== chartId ? t : range === s.range ? followPageRange(t) : { ...t, rangeOverride: range },
      ),
    })),
  setLineStyle: (displayId, style) => set((s) => ({ lineStyle: { ...s.lineStyle, [displayId]: style } })),
  resetLineStyle: (displayId) =>
    set((s) => {
      const lineStyle = { ...s.lineStyle };
      delete lineStyle[displayId];
      return { lineStyle };
    }),
  setChartSetting: (key, value) => set((s) => ({ chartSettings: { ...s.chartSettings, [key]: value } })),
  resetChartSettings: () =>
    set({ chartSettings: { ...DEFAULT_CHART_SETTINGS, bandColors: [...DEFAULT_CHART_SETTINGS.bandColors] } }),

  toggleCollapsed: (chartId) => set((s) => ({ collapsed: { ...s.collapsed, [chartId]: !s.collapsed[chartId] } })),
  toggleSidebarCollapsed: (chartId) =>
    set((s) => {
      const sidebarCollapsed = { ...s.sidebarCollapsed };
      if (sidebarCollapsed[chartId]) delete sidebarCollapsed[chartId];
      else sidebarCollapsed[chartId] = true;
      return { sidebarCollapsed };
    }),
  setAllSidebarCollapsed: (collapsed) =>
    set((s) => ({
      sidebarCollapsed: collapsed ? Object.fromEntries(s.charts.map((t) => [t.id, true as const])) : {},
    })),
  setSeriesHidden: (chartId, displayId, hide) =>
    set((s) => {
      const key = ops.seriesKey(chartId, displayId);
      if (!!s.hidden[key] === hide) return {};
      const hidden = { ...s.hidden };
      if (hide) hidden[key] = true;
      else delete hidden[key];
      return { hidden };
    }),
  setSeriesSidebarWidth: (width) =>
    set(
      Number.isFinite(width)
        ? { seriesSidebarWidth: Math.round(Math.min(SERIES_SIDEBAR_WIDTH.max, Math.max(SERIES_SIDEBAR_WIDTH.min, width))) }
        : {},
    ),
  toggleExpanded: (chartId) =>
    set((s) => ({
      expandedChartIds: s.expandedChartIds.includes(chartId)
        ? s.expandedChartIds.filter((id) => id !== chartId)
        : [...s.expandedChartIds, chartId],
    })),
  setOpenMenu: (key) => set({ openMenu: key, openReadout: null }),
  setOpenReadout: (key) => set({ openReadout: key, openMenu: null }),

  startDrag: (drag) => set({ drag }),
  setDragOver: (targetId) => set((s) => (s.dragOver === targetId ? {} : { dragOver: targetId })),
  endDrag: () => set({ drag: null, dragOver: null }),
  dropOn: (targetId) =>
    set((s) => {
      const drag = s.drag;
      const cleared = { drag: null, dragOver: null };
      if (!drag) return cleared;
      const gap = gapIndex(targetId);
      const op =
        targetId === NEW_CHART_ZONE
          ? (makeId: () => string) => ops.newChartFrom(s.charts, drag.displayId, drag.fromChartId, makeId)
          : gap != null
            ? (makeId: () => string) => ops.newChartAt(s.charts, drag.displayId, drag.fromChartId, gap, makeId)
            : () => ops.moveSeries(s.charts, drag.fromChartId, drag.displayId, targetId);
      return { ...applyOp(s, op), ...cleared };
    }),

  clearAll: () =>
    set((s) => ({
      ...emptyPage(s),
      saved: s.saved,
      trendName: s.trendName,
      sidebarPanel: s.sidebarPanel,
      // The same trend, emptied: not a different page.
      pageGeneration: s.pageGeneration,
    })),
  reset: () => set(initialTrendsData()),
}));

// ---------------------------------------------------------------------------
// Derived values
// ---------------------------------------------------------------------------

/** What a saved config is built from. Nothing else changes it. */
const CONFIG_INPUTS = [
  "charts",
  "nextId",
  "chosenVariant",
  "startingVariant",
  "scale",
  "range",
  "lineStyle",
  "chartSettings",
  "hidden",
  "expandedChartIds",
] as const satisfies readonly (keyof TrendPage)[];

let lastConfig: { inputs: Pick<TrendPage, (typeof CONFIG_INPUTS)[number]>; json: string } | null = null;

/**
 * The page's config as JSON, to compare with the one last saved. Kept until
 * one of its inputs changes, so updates that don't touch the charts (a drag,
 * a resize, a menu opening) don't rebuild it.
 */
function savedConfigOf(page: TrendsData): string {
  if (lastConfig && CONFIG_INPUTS.every((key) => lastConfig!.inputs[key] === page[key])) return lastConfig.json;
  const inputs = Object.fromEntries(CONFIG_INPUTS.map((key) => [key, page[key]])) as Pick<
    TrendPage,
    (typeof CONFIG_INPUTS)[number]
  >;
  const json = JSON.stringify(toTrendConfig(inputs));
  lastConfig = { inputs, json };
  return json;
}

/** The page's name: the one the user gave it, else its dashboard's. */
export const trendTitle = (state: Pick<TrendsData, "trendName" | "dashboardTitle">): string =>
  state.trendName ?? state.dashboardTitle ?? "Untitled trend";

/** The page is an unsaved preview with charts on it. */
export const isOpenPreview = (state: Pick<TrendsData, "saved" | "charts">): boolean =>
  !state.saved && state.charts.length > 0;

/**
 * The page has changes it would lose: changes since it was last saved, or, on
 * a preview, any chart at all. A saved trend emptied of its charts has changes
 * too, though it can't be saved like that (see `canSave`).
 */
export function hasUnsavedChanges(state: TrendsData): boolean {
  if (state.charts.length === 0) return !!state.saved;
  if (!state.saved) return true;
  return state.trendName !== state.saved.name || savedConfigOf(state) !== state.saved.config;
}

/** Save has something it can save: unsaved changes, and at least one chart (an empty trend isn't saved). */
export function canSave(state: TrendsData): boolean {
  return state.charts.length > 0 && hasUnsavedChanges(state);
}

/** The variant the user picked for a series, else the one it started on. Undefined: its default. */
export function explicitVariant(
  state: Pick<TrendsData, "chosenVariant" | "startingVariant">,
  displayId: string,
): TrendVariant | undefined {
  return state.chosenVariant[displayId] ?? state.startingVariant[displayId];
}

/** A series' variant, undefined until it's known (its default, until its config loads). */
export type VariantOf = (displayId: string) => TrendVariant | undefined;

/** Shown for a trend none of whose variants are known yet. */
const UNKNOWN_VARIANT: TrendVariant = "rawlevel";

function sharedOrMixed<T>(values: T[]): T | "mixed" | null {
  const unique = Array.from(new Set(values));
  if (unique.length === 0) return null;
  return unique.length === 1 ? unique[0] : "mixed";
}

const knownVariants = (variantOf: VariantOf, displayIds: string[]) =>
  displayIds.flatMap((id) => {
    const variant = variantOf(id);
    return variant ? [variant] : [];
  });

/** The trend's variant, or "mixed" when its series differ. Series whose variant isn't known yet don't count. */
export function chartVariantState(variantOf: VariantOf, chart: Chart): TrendVariant | "mixed" {
  return sharedOrMixed(knownVariants(variantOf, chart.seriesIds)) ?? UNKNOWN_VARIANT;
}

/** The variant across every trend on the page, or "mixed". */
export function pageVariantState(variantOf: VariantOf, charts: Chart[]): TrendVariant | "mixed" {
  return sharedOrMixed(knownVariants(variantOf, charts.flatMap((t) => t.seriesIds))) ?? UNKNOWN_VARIANT;
}

export function chartScale(state: Pick<TrendsData, "scale">, chart: Chart): TrendScale {
  return chart.scaleOverride ?? state.scale;
}

export function chartRange(state: Pick<TrendsData, "range">, chart: Chart): TrendRange {
  return chart.rangeOverride ?? state.range;
}

/** The page's date range, or "mixed" when a chart shows its own. */
export function pageRangeState(state: Pick<TrendsData, "range" | "charts">): TrendRange | "mixed" {
  return state.charts.some((t) => t.rangeOverride && t.rangeOverride !== state.range) ? "mixed" : state.range;
}

/** The scale across every trend on the page, or "mixed". */
export function pageScaleState(state: Pick<TrendsData, "scale" | "charts">): TrendScale | "mixed" {
  return sharedOrMixed(state.charts.map((t) => chartScale(state, t))) ?? state.scale;
}

/**
 * Different variants plotted as raw values share an axis they can't share.
 * Not said while any series' variant isn't known yet.
 */
export function isMixedUnits(variantOf: VariantOf, state: Pick<TrendsData, "scale">, chart: Chart): boolean {
  if (chart.seriesIds.some((id) => !variantOf(id))) return false;
  return chartVariantState(variantOf, chart) === "mixed" && chartScale(state, chart) === "values";
}
